import React, { useState } from 'react';
import { 
  Calendar as CalendarIcon, ChevronLeft, ChevronRight, ChevronDown,
  Search, Users, Sun, Bell, BellRing, BellOff, Building2, UsersRound
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../components/ui/collapsible';
import { Checkbox } from '../../components/ui/checkbox';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { cn } from '../../lib/utils';
import { EVENT_TYPES, USER_COLORS, getUserColor, NOTIFICATION_TYPES } from './calendarConfig';

// Helper function to get time ago string
const getTimeAgo = (dateStr) => {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

// ============================================================================
// UPCOMING EVENTS WIDGET (Today's Agenda)
// ============================================================================
export const UpcomingEventsWidget = ({ events, onEventClick }) => {
  const now = new Date();
  const todayStr = now.toISOString().split('T')[0];
  
  // Get today's events sorted by start time
  const todayEvents = events
    .filter(e => {
      const startStr = new Date(e.start_date).toISOString().split('T')[0];
      return startStr === todayStr;
    })
    .sort((a, b) => new Date(a.start_date) - new Date(b.start_date));

  // Separate past and upcoming events
  const upcomingEvents = todayEvents.filter(e => new Date(e.end_date) > now);
  const nextEvent = upcomingEvents[0];

  if (todayEvents.length === 0) {
    return (
      <div className="p-3 text-center text-muted-foreground text-sm">
        <Sun className="h-8 w-8 mx-auto mb-2 opacity-30" />
        <p>No events today</p>
      </div>
    );
  }

  return (
    <div className="p-3 space-y-2">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-semibold flex items-center gap-2">
          <Sun className="h-4 w-4 text-amber-500" />
          Today&apos;s Agenda
        </span>
        <Badge variant="secondary" className="text-xs">
          {todayEvents.length} event{todayEvents.length !== 1 ? 's' : ''}
        </Badge>
      </div>
      
      {nextEvent && (
        <div 
          className="p-2 rounded-lg bg-primary/10 border border-primary/20 cursor-pointer hover:bg-primary/20 transition-colors"
          onClick={() => onEventClick(nextEvent)}
        >
          <div className="flex items-center gap-2 mb-1">
            <BellRing className="h-3 w-3 text-primary animate-pulse" />
            <span className="text-xs font-medium text-primary">Up Next</span>
          </div>
          <div className="text-sm font-medium">{nextEvent.title}</div>
          <div className="text-xs text-muted-foreground">
            {new Date(nextEvent.start_date).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
            {nextEvent.location && ` • ${nextEvent.location}`}
          </div>
        </div>
      )}
      
      <ScrollArea className="max-h-32">
        <div className="space-y-1">
          {todayEvents.slice(nextEvent ? 1 : 0, 5).map(event => {
            const eventType = EVENT_TYPES[event.event_type] || EVENT_TYPES.other;
            const isPast = new Date(event.end_date) < now;
            
            return (
              <div
                key={event.id}
                className={cn(
                  "flex items-center gap-2 p-1.5 rounded hover:bg-accent cursor-pointer text-sm",
                  isPast && "opacity-50"
                )}
                onClick={() => onEventClick(event)}
              >
                <div 
                  className="w-1.5 h-6 rounded-full"
                  style={{ backgroundColor: eventType.color }}
                />
                <div className="flex-1 min-w-0">
                  <div className={cn("truncate text-xs font-medium", isPast && "line-through")}>
                    {event.title}
                  </div>
                  <div className="text-[10px] text-muted-foreground">
                    {new Date(event.start_date).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                  </div>
                </div>
                {isPast && <Badge variant="outline" className="text-[10px] px-1">Done</Badge>}
              </div>
            );
          })}
        </div>
      </ScrollArea>
    </div>
  );
};

// ============================================================================
// CALENDAR NOTIFICATIONS PANEL
// ============================================================================
export const CalendarNotificationsPanel = ({ notifications, onMarkRead, onMarkAllRead, onClear }) => {
  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          {unreadCount > 0 ? (
            <BellRing className="h-4 w-4 text-primary" />
          ) : (
            <Bell className="h-4 w-4" />
          )}
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-destructive text-[10px] text-white flex items-center justify-center">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="p-3 border-b flex items-center justify-between">
          <h4 className="font-semibold text-sm">Calendar Notifications</h4>
          {notifications.length > 0 && (
            <Button variant="ghost" size="sm" className="text-xs h-7" onClick={onMarkAllRead}>
              Mark all read
            </Button>
          )}
        </div>
        
        <ScrollArea className="max-h-80">
          {notifications.length === 0 ? (
            <div className="p-6 text-center text-muted-foreground">
              <BellOff className="h-8 w-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No notifications</p>
            </div>
          ) : (
            <div className="divide-y">
              {notifications.map(notification => {
                const notifType = NOTIFICATION_TYPES[notification.type] || NOTIFICATION_TYPES.reminder;
                const Icon = notifType.icon;
                const timeAgo = getTimeAgo(notification.created_at);
                
                return (
                  <div
                    key={notification.id}
                    className={cn(
                      "p-3 hover:bg-accent/50 cursor-pointer transition-colors",
                      !notification.read && "bg-primary/5"
                    )}
                    onClick={() => onMarkRead(notification.id)}
                  >
                    <div className="flex gap-3">
                      <div className={cn("p-1.5 rounded-full", notifType.bg)}>
                        <Icon className={cn("h-4 w-4", notifType.color)} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className={cn("text-sm", !notification.read && "font-medium")}>
                            {notification.title}
                          </p>
                          {!notification.read && (
                            <span className="h-2 w-2 rounded-full bg-primary flex-shrink-0 mt-1.5" />
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5 truncate">
                          {notification.message}
                        </p>
                        <p className="text-[10px] text-muted-foreground mt-1">
                          {timeAgo}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
        
        {notifications.length > 0 && (
          <div className="p-2 border-t">
            <Button variant="ghost" size="sm" className="w-full text-xs" onClick={onClear}>
              Clear all notifications
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

// ============================================================================
// MINI CALENDAR SIDEBAR COMPONENT
// ============================================================================
export const MiniCalendar = ({ currentDate, onDateSelect, events }) => {
  const [localMiniDate, setLocalMiniDate] = useState(null);
  const miniDate = localMiniDate || new Date(currentDate);

  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();
  const miniMonth = miniDate.getMonth();
  const miniYear = miniDate.getFullYear();
  
  if (localMiniDate && (currentMonth !== miniMonth || currentYear !== miniYear)) {
    const monthDiff = Math.abs((currentYear - miniYear) * 12 + (currentMonth - miniMonth));
    if (monthDiff > 1) {
      setLocalMiniDate(null);
    }
  }

  const year = miniDate.getFullYear();
  const month = miniDate.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrevMonth = new Date(year, month, 0).getDate();
  
  const today = new Date();
  const todayStr = today.toISOString().split('T')[0];
  const selectedStr = currentDate.toISOString().split('T')[0];

  const navigateMiniMonth = (dir) => {
    const newDate = new Date(miniDate);
    newDate.setMonth(newDate.getMonth() + dir);
    setLocalMiniDate(newDate);
  };

  const hasEvents = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return events.some(e => {
      const start = new Date(e.start_date).toISOString().split('T')[0];
      const end = new Date(e.end_date).toISOString().split('T')[0];
      return dateStr >= start && dateStr <= end;
    });
  };

  const days = [];
  
  for (let i = firstDay - 1; i >= 0; i--) {
    const day = daysInPrevMonth - i;
    const date = new Date(year, month - 1, day);
    days.push({ day, date, isCurrentMonth: false });
  }
  
  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(year, month, day);
    days.push({ day, date, isCurrentMonth: true });
  }
  
  const remaining = 42 - days.length;
  for (let day = 1; day <= remaining; day++) {
    const date = new Date(year, month + 1, day);
    days.push({ day, date, isCurrentMonth: false });
  }

  return (
    <div className="p-3">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-semibold">
          {miniDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </span>
        <div className="flex gap-1">
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => navigateMiniMonth(-1)}>
            <ChevronLeft className="h-3 w-3" />
          </Button>
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => navigateMiniMonth(1)}>
            <ChevronRight className="h-3 w-3" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-0 mb-1">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
          <div key={i} className="text-[10px] text-muted-foreground text-center font-medium py-1">
            {d}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-0">
        {days.map(({ day, date, isCurrentMonth }, i) => {
          const dateStr = date.toISOString().split('T')[0];
          const isToday = dateStr === todayStr;
          const isSelected = dateStr === selectedStr;
          const hasEvent = hasEvents(date);

          return (
            <button
              key={i}
              onClick={() => onDateSelect(date)}
              className={cn(
                "relative h-7 w-7 text-xs rounded-full flex items-center justify-center transition-colors",
                !isCurrentMonth && "text-muted-foreground/50",
                isCurrentMonth && "text-foreground hover:bg-accent",
                isToday && "bg-primary text-primary-foreground font-semibold",
                isSelected && !isToday && "ring-2 ring-primary",
              )}
            >
              {day}
              {hasEvent && !isToday && (
                <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-primary" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ============================================================================
// PEOPLE CALENDARS SIDEBAR SECTION
// ============================================================================
export const PeopleCalendarsSection = ({ users, selectedUsers, onToggleUser, currentUserId }) => {
  const [isOpen, setIsOpen] = useState(false);  // Start collapsed
  const [searchQuery, setSearchQuery] = useState('');
  
  const filteredUsers = users.filter(u => 
    u.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.last_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="border-t">
      <CollapsibleTrigger className="flex items-center justify-between w-full p-3 hover:bg-accent/50 transition-colors">
        <div className="flex items-center gap-2">
          <UsersRound className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-semibold">People</span>
          {selectedUsers.length > 0 && (
            <Badge variant="secondary" className="h-5 px-1.5 text-xs">
              {selectedUsers.length}
            </Badge>
          )}
        </div>
        <ChevronDown className={cn("h-4 w-4 text-muted-foreground transition-transform duration-200", isOpen && "rotate-180")} />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="px-3 pb-3">
          <div className="relative mb-2">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
            <Input
              placeholder="Search people..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-7 pl-7 text-xs"
            />
          </div>
          
          <ScrollArea className="max-h-48">
            <div className="space-y-1">
              {filteredUsers.slice(0, 15).map((user) => {
                const userColor = getUserColor(user.id);
                const isSelected = selectedUsers.includes(user.id);
                const isCurrentUser = user.id === currentUserId;
                
                return (
                  <label
                    key={user.id}
                    className="flex items-center gap-2 py-1.5 px-1 rounded hover:bg-accent cursor-pointer"
                  >
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => onToggleUser(user.id)}
                      className="h-4 w-4"
                      style={{ 
                        borderColor: userColor.color, 
                        backgroundColor: isSelected ? userColor.color : 'transparent' 
                      }}
                    />
                    <Avatar className="h-5 w-5">
                      <AvatarImage src={user.profile_picture} />
                      <AvatarFallback 
                        className="text-[10px]"
                        style={{ backgroundColor: userColor.lightColor, color: userColor.color }}
                      >
                        {user.first_name?.[0]}{user.last_name?.[0]}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-xs flex-1 truncate">
                      {user.first_name} {user.last_name}
                      {isCurrentUser && <span className="text-muted-foreground ml-1">(You)</span>}
                    </span>
                  </label>
                );
              })}
              {filteredUsers.length === 0 && (
                <div className="text-xs text-muted-foreground text-center py-2">
                  No people found
                </div>
              )}
            </div>
          </ScrollArea>
          
          {selectedUsers.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="w-full mt-2 h-7 text-xs"
              onClick={() => selectedUsers.forEach(id => onToggleUser(id))}
            >
              Clear selection
            </Button>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

// ============================================================================
// TEAM CALENDARS SIDEBAR SECTION
// ============================================================================
export const TeamCalendarsSection = ({ teams, selectedTeams, onToggleTeam }) => {
  const [isOpen, setIsOpen] = useState(false);  // Start collapsed

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="border-t">
      <CollapsibleTrigger className="flex items-center justify-between w-full p-3 hover:bg-accent/50 transition-colors">
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-semibold">Teams</span>
          {selectedTeams.length > 0 && (
            <Badge variant="secondary" className="h-5 px-1.5 text-xs">
              {selectedTeams.length}
            </Badge>
          )}
        </div>
        <ChevronDown className={cn("h-4 w-4 text-muted-foreground transition-transform duration-200", isOpen && "rotate-180")} />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="px-3 pb-3 space-y-1">
          {teams.map((team, idx) => {
            const teamColor = USER_COLORS[idx % USER_COLORS.length];
            const isSelected = selectedTeams.includes(team.id);
            
            return (
              <label
                key={team.id}
                className="flex items-center gap-2 py-1.5 px-1 rounded hover:bg-accent cursor-pointer"
              >
                <Checkbox
                  checked={isSelected}
                  onCheckedChange={() => onToggleTeam(team.id)}
                  className="h-4 w-4"
                  style={{ 
                    borderColor: teamColor.color, 
                    backgroundColor: isSelected ? teamColor.color : 'transparent' 
                  }}
                />
                <div 
                  className="h-5 w-5 rounded flex items-center justify-center"
                  style={{ backgroundColor: teamColor.lightColor }}
                >
                  <Users className="h-3 w-3" style={{ color: teamColor.color }} />
                </div>
                <span className="text-xs flex-1 truncate">{team.name}</span>
                <span className="text-[10px] text-muted-foreground">
                  {team.member_count || 0}
                </span>
              </label>
            );
          })}
          {teams.length === 0 && (
            <div className="text-xs text-muted-foreground text-center py-2">
              No teams available
            </div>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

// ============================================================================
// MY CALENDARS SIDEBAR SECTION
// ============================================================================
export const MyCalendarsSection = ({ calendars, visibleCalendars, onToggle }) => {
  const [isOpen, setIsOpen] = useState(true);  // Keep expanded by default

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="border-t">
      <CollapsibleTrigger className="flex items-center justify-between w-full p-3 hover:bg-accent/50 transition-colors">
        <div className="flex items-center gap-2">
          <CalendarIcon className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-semibold">My calendars</span>
        </div>
        <ChevronDown className={cn("h-4 w-4 text-muted-foreground transition-transform duration-200", isOpen && "rotate-180")} />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="px-3 pb-3 space-y-1">
          {calendars.map((cal) => (
            <label
              key={cal.id}
              className="flex items-center gap-2 py-1.5 px-1 rounded hover:bg-accent cursor-pointer"
            >
              <Checkbox
                checked={visibleCalendars.includes(cal.id)}
                onCheckedChange={() => onToggle(cal.id)}
                className="h-4 w-4"
                style={{ borderColor: cal.color, backgroundColor: visibleCalendars.includes(cal.id) ? cal.color : 'transparent' }}
              />
              <span className="text-xs">{cal.name}</span>
            </label>
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};
